import type { RouteRecordRaw } from 'vue-router'
import type { Menu } from '@/api/common/model/userModel'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import { store } from '../index'
import { enableStoreHMRUpdate } from '../helper'
import { getMenu } from '@/api/common/user'
// import { asyncRoutes } from '@/router/routes'
import { isUrl } from '@/utils/is'
import { omit } from '@/utils/set'
import { forEach } from '@/utils/tree'
// import { useLocaleStoreWithOut } from '@/store-pinia/modules/locale'
import { transformMenuToRoute } from '@/router/helper/routerHelp'
import { Layout } from '@/router/basic'
import { useGlobalI18n } from '@/composables/useI18n'
import { storageProxy } from '@/utils/cache'
import { CacheKeys } from '@/enums/cacheEnum'

export interface State {
  // 按钮权限列表
  permissionCodeList: string[]
  // 动态路由是否已生成
  isDynamicAddedRoute: boolean
  // 菜单
  menuList: Menu[]
  // 收藏菜单
  followedMenuList: Menu[]
  permissionList: Menu[]
  flattenPermissionList: Menu[]
}

export const usePermissionStore = defineStore('permissionStore', {
  state: (): State => ({
    // 权限列表
    permissionCodeList: [],
    // 是否动态构建过路由
    isDynamicAddedRoute: false,
    // 菜单列表
    menuList: [],
    followedMenuList: [],
    // 权限列表
    permissionList: [],
    flattenPermissionList: [],
  }),
  getters: {
    getPermissionCodeList(): string[] {
      return this.permissionCodeList
    },
    getIsDynamicAddedRoute(): boolean {
      return this.isDynamicAddedRoute
    },
    getMenuList(): Menu[] {
      return this.menuList
    },
    getPermissionList(): Menu[] {
      return this.permissionList
    },
    getFollowedMenuList(): Menu[] {
      return this.followedMenuList
    },
    getFlattenPermissionList(): Menu[] {
      return this.flattenPermissionList
    },
  },
  actions: {
    /**
     * 设置权限列表
     * @param list
     */
    setPermissionCodeList(list: string[]) {
      this.permissionCodeList = list
    },
    setPermissionList(permissions: Menu[]) {
      this.permissionList = permissions.slice(0)
    },
    /**
     * 设置是否动态构建过路由
     * @param added
     */
    setIsDynamicAddedRoute(added: boolean) {
      this.isDynamicAddedRoute = added
    },
    /**
     * 设置菜单列表
     * @param menuList
     */
    setMenuList(menuList: Menu[]) {
      this.menuList = menuList
    },
    /**
     * 设置收藏菜单列表
     * @param list
     */
    setFollowedMenuList(list: Menu[]) {
      this.followedMenuList = list
    },
    /**
     * 重置state
     */
    resetState() {
      this.$reset()
    },
    async getPermissionListAction() {
      const { t } = useGlobalI18n()
      const permissionList = await getMenu()
      // const localeStore = useLocaleStoreWithOut()
      forEach(
        permissionList,
        (item) => {
          // const showName =
          //   (localeStore.isChina ? item.permissionNameCn : item.permissionNameEn) || 'unknown'
          const showName = t(item.permissionNameCn)
          item.permissionName = showName
          item.name = showName
        },
        { children: 'childrenPermissionList' },
      )
      this.setPermissionList(permissionList)
      return permissionList
    },
    changePermissionCodeList() {
      const permissionList = toRaw(this.permissionList.slice(0))
      for (let i = 0; i < permissionList.length; i++) {
        const item = permissionList[i]
        this.flattenPermissionList.push(omit<Menu>(item, 'childrenPermissionList'))
        item.identifier && item.showStatus && this.permissionCodeList.push(item.identifier)
        if (item.childrenPermissionList && item.childrenPermissionList.length) {
          permissionList.splice(i + 1, 0, ...item.childrenPermissionList)
        }
      }
      storageProxy.set(CacheKeys.PERMISSION_IDENTIFIER, this.permissionCodeList)
    },
    /**
     * 登录
     * @returns
     */
    async buildRoutes() {
      const menuMap: Record<string, Menu> = {}

      const checkHasPermission = (route: RouteRecordRaw, permissionCodes: string[]) => {
        const routePermissions = route.meta?.permissions

        if (Array.isArray(routePermissions)) {
          return routePermissions.some((code) => permissionCodes.includes(code))
        }

        return true
      }

      const filterAsyncRoutes = (routes: RouteRecordRaw[]) => {
        const res: RouteRecordRaw[] = []

        routes.forEach((route) => {
          const tmp = { ...route }
          if (
            tmp.meta?.ignoreAuth === true ||
            tmp.meta?.ignoreAuth === void 0 ||
            checkHasPermission(tmp, this.permissionCodeList)
          ) {
            const foundMenu = this.flattenPermissionList.find((menu) => menu.url === tmp.path)
            if (foundMenu) {
              tmp.meta = tmp.meta || { title: '' }
              tmp.meta.title = foundMenu.name
              tmp.meta.icon = tmp.meta.icon || menuMap[foundMenu.parentId]?.icon || ''
            }
            if (tmp.children) {
              tmp.children = filterAsyncRoutes(tmp.children)
            }
            res.push(tmp)
          }
        })

        return res
      }

      const joinParentPath = (routes: RouteRecordRaw[], parentPath = '') => {
        for (let index = 0; index < routes.length; index++) {
          const route = routes[index]
          if (!(route.path.startsWith('/') || isUrl(route.path))) {
            route.path = `${parentPath}/${route.path}`
            route.meta && (route.meta.ignoreAuth = false)
          }
          if (route?.children?.length) {
            joinParentPath(route.children, route.path)
          }
        }
      }

      try {
        await this.getPermissionListAction()
        this.changePermissionCodeList()
      } catch (error) {
        console.error(error)
      }

      this.flattenPermissionList.forEach((item) => {
        menuMap[item.id] = item
      })

      // joinParentPath(asyncRoutes)

      // const routes: RouteRecordRaw[] = filterAsyncRoutes(asyncRoutes)
      // const routes = transformMenuToRoute(this.flattenPermissionList).filter(
      //   (route) => !!route.path,
      // )

      const filterRoutes = this.flattenPermissionList
        .filter((route) => !!route.url || route.meta?.frameSrc === 'none')
        .map((item: any) => {
          const itemTemp = { ...item }
          itemTemp.name = `${itemTemp.name}_${itemTemp.id}`
          return itemTemp
        })

      filterRoutes.forEach((route) => {
        route.meta = route.meta || { title: '' }
        route.meta.icon = route.meta.icon || menuMap[route.parentId]?.icon || ''
        route.meta.hideMenu = !route.showStatus
      })
      const childrenRoutes = transformMenuToRoute(filterRoutes)

      const routes = [
        {
          path: '/',
          component: Layout,
          children: childrenRoutes,
        },
      ]

      return routes
    },
  },
})

enableStoreHMRUpdate(usePermissionStore)

export function usePermissionStoreWithOut() {
  return usePermissionStore(store)
}
